/**
 * A entrada pelo telefone — spec 0071 (DD-01, C-02, FR-002, FR-003, RN-01).
 *
 * Para quem perdeu o link. O código só chega a número já confirmado, e a página
 * diz a mesma coisa para qualquer número: "se estiver cadastrado, enviamos". Com
 * o código certo, a página troca para o link novo, que a família pode guardar.
 */

import React, { useEffect, useMemo, useState } from 'react';
import './PaginaFamilia.css';
import { Campo, Casca, ErroDaPagina, criarChamada } from './familia-comum';

/** "31999994321" → "(31) 99999-4321", enquanto digita. */
function mascaraTelefone(texto: string): string {
  const n = texto.replace(/\D/g, '').slice(0, 11);
  if (n.length <= 2) return n.length ? `(${n}` : '';
  const meio = n.length > 10 ? 7 : 6;
  return `(${n.slice(0, 2)}) ${n.slice(2, meio)}${n.length > meio ? `-${n.slice(meio)}` : ''}`;
}

const PaginaEntrar: React.FC = () => {
  const chamar = useMemo(() => criarChamada('/api/familia-entrar', {}), []);
  const [telefone, setTelefone] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [erroTelefone, setErroTelefone] = useState<string | null>(null);
  const [erroCodigo, setErroCodigo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => { document.title = 'Entrar · Transporte escolar · Coopertransmig'; }, []);

  const digitos = telefone.replace(/\D/g, '');

  const pedir = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErro(null);
    if (digitos.length < 10) {
      setErroTelefone('Escreva o WhatsApp com o DDD, por exemplo (31) 99999-0000.');
      document.getElementById('fam-entrar-telefone')?.focus();
      return;
    }
    setErroTelefone(null);
    setOcupado(true);
    try {
      await chamar('/codigo', { method: 'POST', body: JSON.stringify({ telefone: digitos }) });
      setEnviado(true);
      setCodigo('');
    } catch (err) {
      setErro((err as Error).message);
    } finally {
      setOcupado(false);
    }
  };

  const confirmar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    if (codigo.length !== 6) {
      setErroCodigo('O código tem 6 números.');
      document.getElementById('fam-entrar-codigo')?.focus();
      return;
    }
    setErroCodigo(null);
    setOcupado(true);
    try {
      const { token } = await chamar<{ token: string }>('/confirmar', { method: 'POST', body: JSON.stringify({ telefone: digitos, codigo }) });
      window.location.replace(`/familia/${token}`);
    } catch (err) {
      if (err instanceof ErroDaPagina && err.codigo !== 'REDE') {
        setErroCodigo(err.message);
        document.getElementById('fam-entrar-codigo')?.focus();
      } else {
        setErro((err as Error).message);
      }
      setOcupado(false);
    }
  };

  return (
    <Casca>
      <h1 className="fam-titulo">Entrar com o seu telefone</h1>
      {!enviado ? (
        <form className="fam-form" onSubmit={pedir} noValidate>
          <p className="fam-apoio">
            Use o WhatsApp que a cooperativa já confirmou como responsável. Um código chega nele em instantes.
          </p>
          <Campo id="fam-entrar-telefone" rotulo="WhatsApp com DDD" erro={erroTelefone ?? undefined}>
            {(a11y) => (
              <input {...a11y} type="tel" inputMode="tel" autoComplete="tel-national" value={telefone}
                onChange={(e) => setTelefone(mascaraTelefone(e.target.value))} placeholder="(31) 99999-0000" autoFocus />
            )}
          </Campo>
          {erro && <p className="fam-erro" role="alert">{erro}</p>}
          <button type="submit" className="fam-btn fam-btn-primario" disabled={ocupado}>
            {ocupado ? 'Enviando…' : 'Receber o código'}
          </button>
        </form>
      ) : (
        <form className="fam-form" onSubmit={confirmar} noValidate>
          <p className="fam-apoio" role="status">
            Se o número <b>{telefone}</b> estiver cadastrado e confirmado, o código chegou no WhatsApp dele.
          </p>
          <Campo id="fam-entrar-codigo" rotulo="Código que chegou no WhatsApp" erro={erroCodigo ?? undefined}>
            {(a11y) => (
              <input {...a11y} className="fam-codigo" type="text" inputMode="numeric" autoComplete="one-time-code" value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
            )}
          </Campo>
          {erro && <p className="fam-erro" role="alert">{erro}</p>}
          <button type="submit" className="fam-btn fam-btn-primario" disabled={ocupado}>
            {ocupado ? 'Conferindo…' : 'Entrar'}
          </button>
          <button type="button" className="fam-btn fam-btn-texto" onClick={() => { setEnviado(false); setErroCodigo(null); }} disabled={ocupado}>
            Não chegou? Conferir o número
          </button>
        </form>
      )}
    </Casca>
  );
};

export default PaginaEntrar;
